import { toDollars } from '@/util/currency';
import { Center, Spinner, StackProps } from '@chakra-ui/react';
import { useAtomicStatus } from '@common/react/atomic';
import {
    getAmountRecurrenceStr,
    getContributionOrInvestmentStr,
} from '@common/react/goals';
import {
    GoalAccountInvestmentType,
    TransactionSchedulePlan,
} from '@common/types';
import { dt } from '@common/util/dateTime';
import KeyValueStack, { KeyValueStackItem } from '../../common/KeyValueStack';
import { Paragraph } from '../../common/Typography';
import AccountLockup from '../../financialEntities/AccountLockup';
import { useSelectableDepositoryAccounts } from '../../financialEntities/helpers';

export interface RetirementGoalOnboardingConfirmKeyValueStackProps
    extends StackProps {
    initialDepositPlan: number;
    transactionSchedulePlan: TransactionSchedulePlan;
    fundingAccountId: string | null | undefined;
    isAccountComplete: boolean;
    accountTypeItem: KeyValueStackItem;
}

// TODO: Try to remove this component and just use GoalOnboardingConfirmKeyValueStack
/** @deprecated */
const RetirementGoalOnboardingConfirmKeyValueStack = ({
    initialDepositPlan,
    transactionSchedulePlan,
    fundingAccountId,
    isAccountComplete,
    accountTypeItem,
    ...props
}: RetirementGoalOnboardingConfirmKeyValueStackProps) => {
    const atomicStatus = useAtomicStatus();
    const { accounts } = useSelectableDepositoryAccounts();
    const fundingAccount =
        fundingAccountId && accounts.find((a) => a.id === fundingAccountId);

    if (!atomicStatus || !fundingAccount) {
        return (
            <Center h="full" w="full">
                <Spinner color="gray.400" />
            </Center>
        );
    }

    const initialDepositAmountStr = toDollars(initialDepositPlan);

    const recurringInvestmentStr = getAmountRecurrenceStr(
        transactionSchedulePlan
    );

    // TODO: Revisit this if we switch to CLASSIC_PORTFOLIO for IRAs
    const goalAccountInvestmentType: GoalAccountInvestmentType =
        'DIRECT_INDEXING';

    const contributionOrInvestmentStr = getContributionOrInvestmentStr(
        goalAccountInvestmentType
    );

    // Compose items list, omitting the "initial investment" row if initial
    // deposit amount is $0 and/or the "recurring investment" row if the
    // transaction schedule plan amount is $0
    const items: KeyValueStackItem[] = [
        {
            key: 'Source account',
            value: (
                <AccountLockup
                    account={fundingAccount}
                    maxW={52}
                    justifyContent="flex-end"
                />
            ),
        },
    ];

    if (initialDepositPlan > 0) {
        const dateStr = isAccountComplete ? 'today' : 'account opening';
        items.push({
            key: `Initial ${contributionOrInvestmentStr}`,
            value: (
                <Paragraph textAlign="right">
                    {initialDepositAmountStr}
                    <Paragraph color="textMuted">
                        within 1-2 business days of {dateStr}
                    </Paragraph>
                </Paragraph>
            ),
        });
    }

    if (transactionSchedulePlan.amount > 0) {
        // Just to line up the "today" string with initial deposit.
        // TODO: consider formalizing this logic more broadly.
        const { startDate } = transactionSchedulePlan;
        const startDateFromNow = dt(startDate).fromNowDays();
        const recurringInvestmentStartDateStr =
            startDateFromNow === 'today' || startDateFromNow === 'tomorrow'
                ? isAccountComplete
                    ? startDateFromNow
                    : 'account opening'
                : dt(startDate).format('MMMM D, YYYY');

        items.push({
            key: `Repeating ${contributionOrInvestmentStr}`,
            value: (
                <Paragraph textAlign="right">
                    {recurringInvestmentStr}
                    <Paragraph color="textMuted">
                        starts within 1-2 business days of{' '}
                        {recurringInvestmentStartDateStr}
                    </Paragraph>
                </Paragraph>
            ),
        });
    }

    items.push(accountTypeItem);

    return <KeyValueStack spacing={8} items={items} withEndLine {...props} />;
};

export default RetirementGoalOnboardingConfirmKeyValueStack;
